import { Transform } from 'class-transformer';
import {
  IsDateString,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { RECALL_KINDS, RECALL_SOURCES, type RecallKind, type RecallSource } from './recall.enums';

/**
 * In DRAFT ogni campo è modificabile; in OPEN il backend accetta solo
 * instructions, notes, authorityNotifiedAt, authorityNoticeWaiverReason,
 * brand, unitDescription, producerName e producerAddress — questi ultimi
 * quattro non entrano nel PDF di apertura già firmato, servono solo al
 * cartello "AVVISO DI RICHIAMO" generato su richiesta.
 */
export class UpdateRecallCaseDto {
  @IsIn(RECALL_KINDS)
  @IsOptional()
  kind?: RecallKind;

  @IsIn(RECALL_SOURCES)
  @IsOptional()
  source?: RecallSource;

  @IsString()
  @IsOptional()
  @MaxLength(200)
  @Transform(({ value }) => (typeof value === 'string' ? value.trim() : value))
  productName?: string;

  @IsString()
  @IsOptional()
  @MaxLength(100)
  @OptionalStringTransform()
  supplierLotNumber?: string;

  @IsUUID()
  @IsOptional()
  supplierId?: string;

  /** Marchio commerciale del prodotto, stampato sul cartello. `null` lo cancella. */
  @IsString()
  @IsOptional()
  @MaxLength(200)
  @OptionalStringTransform()
  brand?: string | null;

  /** Confezione/formato dell'unità di vendita (es. "Vasetto da 250 g"). `null` lo cancella. */
  @IsString()
  @IsOptional()
  @MaxLength(200)
  @OptionalStringTransform()
  unitDescription?: string | null;

  /** Produttore stampato sul cartello quando diverso dall'OSA. `null` lo cancella. */
  @IsString()
  @IsOptional()
  @MaxLength(200)
  @OptionalStringTransform()
  producerName?: string | null;

  /** Sede del produttore, accanto a `producerName`. `null` lo cancella. */
  @IsString()
  @IsOptional()
  @MaxLength(300)
  @OptionalStringTransform()
  producerAddress?: string | null;

  @IsString()
  @IsOptional()
  @MaxLength(2000)
  @OptionalStringTransform()
  hazardDescription?: string;

  @IsString()
  @IsOptional()
  @MaxLength(2000)
  @OptionalStringTransform()
  instructions?: string;

  @IsString()
  @IsOptional()
  @MaxLength(4000)
  @OptionalStringTransform()
  notes?: string;

  /**
   * ISO date-time; notifica all'autorità sanitaria, registrabile solo mentre il caso è OPEN.
   * `null` la cancella. Valorizzarla cancella `authorityNoticeWaiverReason`.
   */
  @IsDateString()
  @IsOptional()
  authorityNotifiedAt?: string | null;

  /**
   * Perché la notifica all'Autorità non è dovuta (es. art. 19(2) Reg. CE
   * 178/2002: il fornitore ha già notificato l'ASL). `null` la cancella;
   * valorizzarla cancella `authorityNotifiedAt`. Una stringa vuota è rifiutata:
   * la chiusura non può poggiare su una motivazione in bianco.
   */
  @IsString()
  @IsOptional()
  @MinLength(1)
  @MaxLength(1000)
  @Transform(({ value }) => (typeof value === 'string' ? value.trim() : value))
  authorityNoticeWaiverReason?: string | null;
}
